// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';

// Set to true only during development
const DEBUG = false;

export const AuthContext = createContext({});

// Helper: Wacht een korte tijd (Kan eventueel weg als niet meer nodig)
// const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isSessionValid, setIsSessionValid] = useState(true);
  const [sessionExpiryTime, setSessionExpiryTime] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const navigate = useNavigate();
  const { setMode } = useTheme();
  
  // --- Refs --- (Simplified, mainly for tracking)
  const initStartedRef = useRef(false);
  const authListenerSubscriptionRef = useRef(null);

  // --- Callbacks --- (Simplified where possible)
  
  // Function to fetch user role - stays async
  const fetchUserRole = useCallback(async (userId) => {
    if (!userId) return 'user'; 
    setRoleLoading(true); // Start role loading
    try {
      // console.log('[AuthContext] Fetching role for user:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('role, theme_preference')
        .eq('id', userId)
        .single();
      if (error) {
        console.error('[AuthContext] Error fetching role:', error); // Keep error logs
        setRole('user'); 
        setRoleLoading(false); // Finish role loading even on error
        return 'user';
      }
      const fetchedRole = data?.role || 'user';
      // Set theme preference if it exists
      if (data?.theme_preference) {
        setMode(data.theme_preference);
      }
      // console.log('[AuthContext] Fetched role:', fetchedRole);
      setRole(fetchedRole);
      // console.log(`[AuthContext] Role set to: ${fetchedRole} for user: ${userId}`);
      setRoleLoading(false);
      return fetchedRole;
    } catch (error) {
      console.error('[AuthContext] Unexpected error in fetchUserRole:', error); // Keep error logs
      setRole('user'); 
      setRoleLoading(false); // Finish role loading even on error
      return 'user';
    }
  }, [setMode]);
  
  // Function to handle session data and update state SYNCHRONOUSLY
  const handleSessionData = useCallback((session) => {
    // console.log("[AuthContext] handleSessionData called with session:", session ? `User: ${session.user?.id}` : "null");
     const currentUser = session?.user || null;
     setUser(currentUser);
     setSessionExpiryTime(session?.expires_at ? new Date(session.expires_at * 1000).getTime() : null);
     setIsSessionValid(!!currentUser);
     setShowSessionWarning(false); // Reset warning on session update

     // Mark initial session check as done and stop loading
     if (!sessionChecked) {
       // console.log("[AuthContext] First session check complete, setting sessionChecked=true, loading=false");
       setSessionChecked(true);
       setLoading(false);
     }
     // console.log("[AuthContext] handleSessionData finished.");
   }, [sessionChecked]); // Depends on sessionChecked to correctly set it once

  // --- Effects --- 

  // Effect 1: Run ONLY ONCE on mount for initial session check
  useEffect(() => {
    if (initStartedRef.current) return; 
    initStartedRef.current = true;
    let mounted = true;
    
    // console.log('[AuthContext] Effect 1: Running initial getSession setup...');

    const initSession = async () => {
      try {
        // console.log('[AuthContext] Effect 1: Waiting 50ms before getSession...');
        // await sleep(50); // Remove sleep if not needed
        
        if (!mounted) return; 
        
        // console.log('[AuthContext] Effect 1: Attempting supabase.auth.getSession()...');
        let session = null;
        let sessionError = null;
        try {
            const { data, error } = await supabase.auth.getSession();
            if (error) { 
                console.error('[AuthContext] Effect 1: supabase.auth.getSession() returned an error:', error); // Keep error logs
                sessionError = error;
            } else {
                session = data?.session || null;
                 // console.log('[AuthContext] Effect 1: supabase.auth.getSession() completed successfully.', session ? `User: ${session.user?.id}` : "No session");
            }
        } catch (getSessionError) {
             console.error('[AuthContext] Effect 1: CRITICAL ERROR during supabase.auth.getSession() call:', getSessionError); // Keep critical error logs
             sessionError = getSessionError; 
        }
        
        if (mounted) {
           // console.log(`[AuthContext] Effect 1: Calling handleSessionData. Session object: ${session ? 'Exists' : 'null'}. Error object: ${sessionError ? 'Exists' : 'null'}`);
           handleSessionData(session); 
        }
        
      } catch (outerError) {
        console.error('[AuthContext] Effect 1: Error in outer initSession catch block:', outerError); // Keep error logs
         if (mounted) {
            // console.log('[AuthContext] Effect 1: Calling handleSessionData(null) due to outer catch.');
            handleSessionData(null); 
         }
      }
    };

    initSession();
    
    return () => {
        // console.log("[AuthContext] Effect 1: Cleanup (mount related)");
        mounted = false;
    };
  }, [handleSessionData]); 

  // Effect 2: Setup onAuthStateChange listener AFTER initial check is done
  useEffect(() => {
    if (!sessionChecked) {
      // console.log("[AuthContext] Effect 2: Skipping auth listener setup (session not checked yet).");
      return; // Don't setup listener until initial check is done
    }

    // console.log('[AuthContext] Effect 2: Setting up onAuthStateChange listener...');
    let mounted = true;

    const authListenerCallback = (event, session) => {
      // console.log(`[AuthContext] Effect 2: onAuthStateChange event: ${event}`);
      if (mounted) {
        handleSessionData(session); // Handle updates from the listener
        if (event === 'SIGNED_OUT') {
          setMode('light'); // Reset theme to light on sign out
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(authListenerCallback);
    authListenerSubscriptionRef.current = subscription; // Store for cleanup

    // console.log('[AuthContext] Effect 2: Auth listener is active.');

    return () => {
      // console.log('[AuthContext] Effect 2: Cleaning up auth listener...');
      mounted = false;
      authListenerSubscriptionRef.current?.unsubscribe();
      authListenerSubscriptionRef.current = null;
    };
  }, [sessionChecked, handleSessionData]); // Run when sessionChecked becomes true

  // Effect 3: Fetch user role when user object changes
  useEffect(() => {
    if (user?.id) {
      // console.log('[AuthContext] Effect 3: User changed, fetching role...');
      fetchUserRole(user.id);
    } else {
      // console.log('[AuthContext] Effect 3: No user, setting role to null.');
      setRole(null); // Clear role if no user
      setRoleLoading(false); // Ensure role loading is false if no user
    }
  }, [user, fetchUserRole]); // Depend on user object and fetchUserRole callback

  // --- Login/Logout Functions --- (Simplified, rely on listener for state updates)
  const login = useCallback(async ({ email, password }) => {
    // console.log("[AuthContext] Attempting login...");
    try {
      // Sign in, the onAuthStateChange listener will handle the state update
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // No need to manually set state here, listener will do it
      // console.log("[AuthContext] Login successful, waiting for listener...");
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Login error:', error); // Keep error logs
      return { error }; // Return error to the component
    }
  }, []);

  const logout = useCallback(async () => {
    // console.log("[AuthContext] Attempting logout...");
    try {
      // Reset theme to light before signing out
      setMode('light');
      // Sign out, the onAuthStateChange listener will handle the state update
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // No need to manually set state here, listener will clear it
       // console.log("[AuthContext] Logout successful, waiting for listener...");
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Logout error:', error); // Keep error logs
      return { error };
    }
  }, [setMode]);
  
  // --- Context Value --- (Removed SessionManager related values)
  const contextValue = {
    user,
    role,
    loading,
    roleLoading,
    sessionChecked,
    isSessionValid,
    sessionExpiryTime,
    // Removed showSessionWarning and related functions for simplicity
    login,
    logout,
    // Removed checkSession/refreshSession as they are implicitly handled
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {/* Removed Session Warning Popup */}
    </AuthContext.Provider>
  );
};